import React, { useState, useEffect } from 'react';
import { toLocalYMD } from '../lib/dates';
import { X, User, MapPin, Mail, Phone, StickyNote, Check, Loader2, Plus, Briefcase, Calendar, PoundSterling, ChevronRight, Navigation, Trash2, Power, PowerOff, AlertTriangle } from 'lucide-react';
import AddressPickerModal from './AddressPickerModal';
import DaySuggestions from './DaySuggestions';
import MiniMapPreview from './MiniMapPreview';
import { openGoogleMapsNavigation } from '../utils/navigation';
import { customerChanged, jobChanged, Actions } from '../utils/dataSync';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewCustomerForm = ({ onClose, onSave, customer: existingCustomer, onDelete, onStatusChange }) => {
  // Edit mode flag
  const isEditMode = !!existingCustomer;
  
  // Customer active status
  const [isActive, setIsActive] = useState(existingCustomer?.is_active !== false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Form state - pre-fill if editing
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [greetingName, setGreetingName] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedRound, setSelectedRound] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  // Per-customer default service override (used in reminder text + as the
  // default when adding a new job for this customer). Overrides the
  // tenant-wide user.default_service_name setting.
  const [defaultService, setDefaultService] = useState('');
  const [availableServices, setAvailableServices] = useState([]);
  
  // Update form when customer prop is available (for edit mode)
  useEffect(() => {
    if (existingCustomer) {
      console.log('Loading existing customer data:', existingCustomer);
      setName(existingCustomer.name || '');
      setAddress(existingCustomer.address || '');
      setPostcode(existingCustomer.postcode || '');
      setEmail(existingCustomer.email || '');
      setPhone(existingCustomer.phone || '');
      setGreetingName(existingCustomer.greeting_name || '');
      setNotes(existingCustomer.notes || '');
      setIsActive(existingCustomer.is_active !== false);
      // Handle zone - extract first zone if it contains multiple (e.g., "BH Zone 1 and BH Zone 2")
      const customerZone = existingCustomer.zone || '';
      const firstZone = customerZone.includes(' and ') ? customerZone.split(' and ')[0].trim() : customerZone;
      setSelectedRound(existingCustomer.round_id || firstZone || '');
      setLatitude(existingCustomer.latitude || null);
      setLongitude(existingCustomer.longitude || null);
      setDefaultService(existingCustomer.services || existingCustomer.service || '');
    }
  }, [existingCustomer]);
  
  // Rounds from API
  const [rounds, setRounds] = useState([]);
  const [loadingRounds, setLoadingRounds] = useState(true);
  
  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Success state - show add job option
  const [savedCustomer, setSavedCustomer] = useState(null);
  const [showAddJob, setShowAddJob] = useState(false);
  
  // Job form state (no prefill — user must explicitly pick the service)
  const [jobService, setJobService] = useState('');
  const [jobPrice, setJobPrice] = useState('');
  const [jobFrequency, setJobFrequency] = useState('6');
  const [jobDate, setJobDate] = useState('');
  const [isSavingJob, setIsSavingJob] = useState(false);

  // Fetch rounds from API on mount
  useEffect(() => {
    const fetchRounds = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const response = await fetch(`${API_URL}/api/rounds?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Sort alphabetically
          data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setRounds(data);
          
          // If editing, try to match the round by zone name or round_id
          if (isEditMode && existingCustomer) {
            // Handle multi-zone - get first zone
            const customerZone = existingCustomer.zone || '';
            const firstZone = customerZone.includes(' and ') ? customerZone.split(' and ')[0].trim() : customerZone;
            
            const matchedRound = data.find(r => 
              r.id === existingCustomer.round_id || 
              r.name === existingCustomer.zone ||
              r.name === firstZone
            );
            if (matchedRound) {
              setSelectedRound(matchedRound.id);
              console.log('Matched round:', matchedRound.name);
            } else {
              console.log('No matching round found for zone:', existingCustomer.zone);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching rounds:', err);
      } finally {
        setLoadingRounds(false);
      }
    };
    fetchRounds();
  }, [isEditMode, existingCustomer]);

  // Fetch the user's service catalogue for the "Default Service" dropdown.
  // Cheap one-shot fetch — mirrors the pattern used in JobDetailModal/Panel.
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const response = await fetch(`${API_URL}/api/services`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setAvailableServices(data);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };
    fetchServices();
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!address.trim()) newErrors.address = 'Address is required';
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSaving(true);
    setErrors({});

    // Find the selected round to get its name for zone
    const roundObj = rounds.find(r => r.id === selectedRound);
    const zoneName = roundObj?.name || selectedRound || existingCustomer?.zone || 'Unassigned';

    // Build geolocation object with town_level flag
    const geolocationData = (latitude && longitude) ? {
      lat: latitude,
      lng: longitude,
      town_level: false  // Manual pin placement = exact location
    } : null;

    const customer = {
      name: name.trim(),
      address: `${address.trim()}${postcode ? ', ' + postcode.trim() : ''}`,
      email: email.trim() || null,
      phone: phone.trim() || null,
      // Empty string clears the override (backend treats "" as "no override")
      greeting_name: greetingName.trim(),
      notes: notes.trim() || null,
      zone: zoneName,
      round_id: selectedRound || null,
      balance: existingCustomer?.balance || 0,
      status: 'active',
      latitude: latitude,
      longitude: longitude,
      geolocation: geolocationData,
      // Per-customer default service. Empty string means "no override —
      // use tenant default_service_name". Send null to clear an existing
      // override; backend's `services` field is `Optional[str]` so null
      // is a valid clear signal.
      services: defaultService.trim() || null,
      service: defaultService.trim() || null,
    };

    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Use PUT for edit, POST for create
      const url = isEditMode 
        ? `${API_URL}/api/customers/${existingCustomer.id}`
        : `${API_URL}/api/customers`;
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(customer)
      });

      if (!response.ok) {
        throw new Error(isEditMode ? 'Failed to update customer' : 'Failed to create customer');
      }

      const saved = await response.json();
      
      // Emit customer change to update ALL dependent areas
      customerChanged(isEditMode ? Actions.UPDATE : Actions.CREATE, saved);
      
      if (isEditMode) {
        // For edit mode, just close and call onSave callback
        if (onSave) onSave(saved);
        onClose();
      } else {
        // For create mode, show success with add job option
        setSavedCustomer(saved);
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      setErrors({ submit: isEditMode ? 'Failed to update customer. Please try again.' : 'Failed to save customer. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddJob = async () => {
    if (!savedCustomer || !jobPrice) return;
    
    setIsSavingJob(true);
    setErrors({});
    
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Calculate next due date based on frequency
      const scheduledDate = jobDate || toLocalYMD(new Date());
      const isOneOff = jobFrequency === '0';
      
      const job = {
        customer_id: savedCustomer.id,
        customer_name: savedCustomer.name,
        address: savedCustomer.address,
        services: jobService,
        service: jobService,
        price: parseFloat(jobPrice),
        frequency: isOneOff ? null : jobFrequency,
        scheduled_date: scheduledDate,
        status: 'scheduled',
        zone: savedCustomer.zone,
        round_id: savedCustomer.round_id,
        is_one_off: isOneOff
      };

      console.log('Creating job with data:', job);

      const response = await fetch(`${API_URL}/api/jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(job)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Job creation failed:', errorData);
        throw new Error(errorData.detail || 'Failed to create job');
      }

      const createdJob = await response.json();
      console.log('Job created successfully:', createdJob);

      // Emit job change to update ALL dependent areas
      jobChanged(Actions.CREATE, createdJob);

      // Close and notify parent
      if (onSave) onSave(savedCustomer);
      onClose();
    } catch (err) {
      console.error('Error creating job:', err);
      setErrors({ submit: err.message || 'Failed to create job. Please try again.' });
    } finally {
      setIsSavingJob(false);
    }
  };

  const handleFinish = () => {
    if (onSave && savedCustomer) onSave(savedCustomer);
    onClose();
  };

  // Success state - customer created
  if (savedCustomer && !showAddJob) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="new-customer-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} />
            </div>
            <h2 className="text-xl font-bold">Customer Added!</h2>
            <p className="text-white/80 mt-1">{savedCustomer.name}</p>
          </div>
          
          {/* Options */}
          <div className="p-6 space-y-3">
            <button
              onClick={() => setShowAddJob(true)}
              className="w-full flex items-center justify-between p-4 bg-emerald-50 hover:bg-emerald-100 border-2 border-emerald-200 rounded-xl transition-colors group"
              data-testid="add-job-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center text-white">
                  <Plus size={20} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-zinc-900">Add a Job</p>
                  <p className="text-sm text-zinc-500">Schedule their first clean</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-emerald-500 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button
              onClick={handleFinish}
              className="w-full p-4 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-medium rounded-xl transition-colors"
              data-testid="finish-btn"
            >
              Done - I&apos;ll add jobs later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Add Job form
  if (savedCustomer && showAddJob) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-job-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <Briefcase size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Add Job</h2>
                  <p className="text-white/80 text-sm">for {savedCustomer.name}</p>
                </div>
              </div>
              <button 
                onClick={handleFinish}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              >
                <X size={22} />
              </button>
            </div>
          </div>
          
          {/* Job Form */}
          <div className="p-6 space-y-5">
            {errors.submit && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
                {errors.submit}
              </div>
            )}

            {/* Service Type */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Service Type
              </label>
              <select
                value={jobService}
                onChange={(e) => setJobService(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                data-testid="job-service-select"
              >
                <option value="Window Clean">Window Clean</option>
                <option value="Gutter Clean">Gutter Clean</option>
                <option value="Fascia Clean">Fascia Clean</option>
                <option value="Conservatory Clean">Conservatory Clean</option>
                <option value="Solar Panel Clean">Solar Panel Clean</option>
                <option value="Pressure Wash">Pressure Wash</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Price <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <PoundSterling size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={jobPrice}
                  onChange={(e) => setJobPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                  data-testid="job-price-input"
                />
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Frequency
              </label>
              <select
                value={jobFrequency}
                onChange={(e) => setJobFrequency(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                data-testid="job-frequency-select"
              >
                <option value="6">6 Weekly</option>
                <option value="12">12 Weekly</option>
                <option value="0">One-off</option>
              </select>
            </div>

            {/* Smart day suggestions based on round proximity */}
            <DaySuggestions
              customerId={savedCustomer.id}
              address={savedCustomer.address}
              value={jobDate}
              onPick={(d) => setJobDate(d)}
            />

            {/* First Clean Date */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                {jobFrequency === '0' ? 'Job Date' : 'First Clean Date'}
              </label>
              <div className="relative">
                <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="date"
                  value={jobDate}
                  onChange={(e) => setJobDate(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                  data-testid="job-date-input"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">Leave blank to add to schedule later</p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200 flex gap-3">
            <button
              onClick={handleFinish}
              className="flex-1 px-4 py-3 bg-white border border-zinc-200 hover:bg-zinc-100 text-zinc-700 font-medium rounded-xl transition-colors"
            >
              Skip
            </button>
            <button
              onClick={handleAddJob}
              disabled={isSavingJob || !jobPrice}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
              data-testid="save-job-btn"
            >
              {isSavingJob ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Add Job
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="new-customer-modal">
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <User size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold">{isEditMode ? 'Edit Customer' : 'Add Customer'}</h2>
                <p className="text-white/80 text-sm">{isEditMode ? 'Update customer details' : 'Create a new customer record'}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              data-testid="close-new-customer"
            >
              <X size={22} />
            </button>
          </div>
        </div>
        
        {/* Form Content */}
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Error message */}
          {errors.submit && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
              {errors.submit}
            </div>
          )}

          {/* Name - Required */}
          <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">
              Customer Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., John Smith"
                className={`w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${
                  errors.name ? 'border-red-300 bg-red-50' : 'border-zinc-200'
                }`}
                data-testid="customer-name-input"
              />
            </div>
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Greeting name — optional override for message greetings */}
          <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">
              Greeting Name <span className="text-zinc-400 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={greetingName}
                onChange={(e) => setGreetingName(e.target.value)}
                placeholder="How messages greet them — e.g. Klenz Cleaning"
                className="w-full pl-11 pr-4 py-3 bg-zinc-50 border border-zinc-200 text-zinc-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                data-testid="customer-greeting-name-input"
              />
            </div>
            <p className="text-xs text-zinc-400 mt-1">
              Blank = first name is used (businesses like "… LTD" automatically get their full name)
            </p>
          </div>

          {/* Address - Required */}
          <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">
              Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin size={18} className="absolute left-4 top-3 text-zinc-400" />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddressPicker(true)}
                  className={`flex-1 pl-11 pr-4 py-3 text-left bg-zinc-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${
                    errors.address ? 'border-red-300 bg-red-50' : 'border-zinc-200'
                  } ${address ? 'text-zinc-900' : 'text-zinc-400'}`}
                  data-testid="customer-address-input"
                >
                  {address || 'Click to set address with map...'}
                </button>
                {latitude && longitude && (
                  <button
                    type="button"
                    onClick={() => openGoogleMapsNavigation(latitude, longitude)}
                    className="px-3 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
                    title="Navigate"
                  >
                    <Navigation size={18} />
                  </button>
                )}
              </div>
              {latitude && longitude && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <MapPin size={12} />
                  Location pinned ({latitude.toFixed(4)}, {longitude.toFixed(4)})
                </p>
              )}
            </div>
            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
          </div>

          {/* Mini Map Preview - Show always, click to edit location */}
          {!showAddressPicker && (
            <div className="relative">
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                <MapPin size={16} className="inline mr-1" />
                Location
              </label>
              <MiniMapPreview
                latitude={latitude}
                longitude={longitude}
                address={address}
                height="128px"
                showNavigate={false}
                onClick={() => setShowAddressPicker(true)}
              />
              <p className="text-xs text-zinc-500 mt-1">
                {latitude && longitude ? 'Tap map to adjust pin location' : 'Tap to set exact location on map'}
              </p>
            </div>
          )}
          
          {/* Placeholder when address picker is open */}
          {showAddressPicker && (
            <div className="relative">
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                <MapPin size={16} className="inline mr-1" />
                Location
              </label>
              <div className="h-[128px] bg-zinc-100 rounded-xl flex items-center justify-center text-zinc-500">
                Setting location...
              </div>
            </div>
          )}

          {/* Postcode */}
          <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">
              Postcode
            </label>
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.toUpperCase())}
              placeholder="e.g., BN1 1AA"
              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all uppercase"
              data-testid="customer-postcode-input"
            />
          </div>

          {/* Round Selection - From API */}
          <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">
              Round
            </label>
            {loadingRounds ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-500">
                <Loader2 size={16} className="animate-spin" />
                Loading rounds...
              </div>
            ) : (
              <select
                value={selectedRound}
                onChange={(e) => setSelectedRound(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                data-testid="customer-round-select"
              >
                <option value="">Select a round...</option>
                {rounds.map(round => (
                  <option key={round.id} value={round.id}>
                    {round.name}
                  </option>
                ))}
              </select>
            )}
            {rounds.length === 0 && !loadingRounds && (
              <p className="text-xs text-zinc-500 mt-1">No rounds found. You can add rounds in the Rounds section.</p>
            )}
          </div>

          {/* Default Service - per-customer override of tenant default_service_name.
              Optional. When set, this is the suggested service for new jobs added to
              this customer AND the service name used in reminder messages
              (resolve_service_name fallback chain in routes/jobs.py). */}
          <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">
              Default Service <span className="font-normal text-zinc-400">(optional)</span>
            </label>
            <div className="relative">
              <Briefcase size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <select
                value={defaultService}
                onChange={(e) => setDefaultService(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none cursor-pointer text-zinc-900"
                data-testid="customer-default-service-select"
              >
                <option value="">Use account default</option>
                {availableServices.map(service => (
                  <option key={service.id} value={service.name}>
                    {service.name}
                  </option>
                ))}
                {/* Preserve a previously-saved value that's no longer in
                    the service catalogue so editing doesn't silently
                    drop it. */}
                {defaultService && !availableServices.some(s => s.name === defaultService) && (
                  <option value={defaultService}>{defaultService}</option>
                )}
              </select>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Overrides your account-wide default service for this customer&apos;s reminders and new jobs.
            </p>
          </div>

          {/* Contact Info - Two columns on larger screens */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Phone
              </label>
              <div className="relative">
                <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="07xxx xxxxxx"
                  className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  data-testid="customer-phone-input"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  data-testid="customer-email-input"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">
              Notes
            </label>
            <div className="relative">
              <StickyNote size={18} className="absolute left-4 top-3 text-zinc-400" />
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Access instructions, special requirements, etc."
                rows={3}
                className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
                data-testid="customer-notes-input"
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200">
          {/* Main action buttons */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white border border-zinc-200 hover:bg-zinc-100 text-zinc-700 font-medium rounded-xl transition-colors"
              data-testid="cancel-new-customer"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
              data-testid="save-new-customer"
            >
              {isSaving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={18} />
                  {isEditMode ? 'Save Changes' : 'Add Customer'}
                </>
              )}
            </button>
          </div>
          
          {/* Customer Status & Delete - Only show in Edit Mode */}
          {isEditMode && (
            <div className="pt-4 border-t border-zinc-200">
              <div className="flex items-center justify-between gap-3">
                {/* Active/Inactive Toggle */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-500">Status:</span>
                  <button
                    onClick={async () => {
                      const newStatus = !isActive;
                      setIsActive(newStatus);
                      if (onStatusChange) {
                        await onStatusChange(existingCustomer.id, newStatus);
                      }
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                      isActive 
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                        : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    }`}
                    data-testid="toggle-customer-status"
                  >
                    {isActive ? (
                      <>
                        <Power size={16} />
                        Active
                      </>
                    ) : (
                      <>
                        <PowerOff size={16} />
                        Inactive
                      </>
                    )}
                  </button>
                </div>
                
                {/* Delete Button */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg font-medium text-sm transition-colors"
                  data-testid="delete-customer-btn"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
              
              {!isActive && (
                <p className="mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                  Inactive customers are hidden from Work Planner and Rounds totals
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-zinc-900">Delete Customer?</h3>
                  <p className="text-sm text-zinc-500">{name || 'This customer'}</p>
                </div>
              </div>
              
              <p className="text-sm text-zinc-600 mb-6">
                This will permanently delete this customer and <strong>all their jobs</strong> from the system. This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-medium rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (onDelete) {
                      await onDelete(existingCustomer.id);
                      setShowDeleteConfirm(false);
                      onClose();
                    }
                  }}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors"
                  data-testid="confirm-delete-customer"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Address Picker Modal */}
      <AddressPickerModal
        isOpen={showAddressPicker}
        onClose={() => setShowAddressPicker(false)}
        onSave={({ address: newAddress, latitude: lat, longitude: lng }) => {
          setAddress(newAddress);
          setLatitude(lat);
          setLongitude(lng);
          // Try to extract postcode from the address
          const postcodeMatch = newAddress.match(/([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})/i);
          if (postcodeMatch) {
            setPostcode(postcodeMatch[1].toUpperCase());
          }
        }}
        initialAddress={address}
        initialLat={latitude}
        initialLng={longitude}
      />
    </div>
  );
};

export default NewCustomerForm;
